import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import type { AdminApplication, AdminApplicationId } from '../../apps/adminApplications';
import {
  SITE_VISITS_RANGES,
  formatVisitCount,
  type EmbassyWatchLiveStats,
  type SiteVisitsRange,
  type SiteVisitsReport as SiteVisitsReportData,
} from '../../types/siteVisits';
import { VisitsBarChart } from './VisitsBarChart';
import { VisitsTopTables } from './VisitsTopTables';

export interface SiteVisitsReportProps {
  applications: readonly AdminApplication[];
  applicationId: AdminApplicationId;
  onSelectApplication: (applicationId: AdminApplicationId) => void;
  days: SiteVisitsRange;
  onSelectDays: (days: SiteVisitsRange) => void;
  report: SiteVisitsReportData | null;
  liveStats?: EmbassyWatchLiveStats | null;
  loading: boolean;
  error: string;
  onRetry: () => void;
}

function TotalCard({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <Card>
      <CardHeader className="p-4 pb-1">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <p className="text-3xl font-bold tabular-nums">{formatVisitCount(value)}</p>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

export function SiteVisitsReport({
  applications, applicationId, onSelectApplication, days, onSelectDays, report, liveStats, loading, error, onRetry,
}: SiteVisitsReportProps) {
  const selected = applications.find((application) => application.id === applicationId) ?? applications[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">사이트 방문</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            공개 홈페이지의 일별 조회수·방문자와 어디로 들어와 무엇을 봤는지.
            {selected?.site ? <> <a className="underline underline-offset-2" href={selected.site} target="_blank" rel="noreferrer">{selected.site}</a></> : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center rounded-lg border bg-muted/40 p-1" role="group" aria-label="사이트 선택">
            {applications.map((application) => (
              <button
                key={application.id}
                type="button"
                aria-pressed={application.id === applicationId}
                className={`min-h-9 rounded-md px-3 text-sm font-medium transition-colors ${
                  application.id === applicationId ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => onSelectApplication(application.id)}
              >
                {application.label}
              </button>
            ))}
          </div>
          <div className="flex items-center rounded-lg border bg-muted/40 p-1" role="group" aria-label="기간 선택">
            {SITE_VISITS_RANGES.map((range) => (
              <button
                key={range}
                type="button"
                aria-pressed={range === days}
                className={`min-h-9 rounded-md px-3 text-sm font-medium transition-colors ${
                  range === days ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => onSelectDays(range)}
              >
                {range}일
              </button>
            ))}
          </div>
        </div>
      </div>

      {error ? (
        <div role="alert" className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={onRetry} disabled={loading}>다시 시도</Button>
        </div>
      ) : null}

      <div className={`grid gap-4 ${applicationId === 'embassy-watch' && liveStats ? 'sm:grid-cols-4' : 'sm:grid-cols-2'}`} aria-busy={loading}>
        <TotalCard label="조회수" value={report?.totals.views ?? 0} hint={`최근 ${days}일 페이지 조회 합계`} />
        <TotalCard label="방문자" value={report?.totals.uniques ?? 0} hint={`최근 ${days}일 하루 단위 고유 방문자 합계`} />
        {applicationId === 'embassy-watch' && liveStats ? (
          <>
            <TotalCard label="실시간 동시 접속" value={liveStats.liveWsClients} hint="현재 열려 있는 WebSocket 활성 탭 수" />
            <TotalCard label="푸시 알림 구독 기기" value={liveStats.pushSubscriberCount} hint={`VAPID 실시간 경보 수신 등록 (${liveStats.regimeStatus})`} />
          </>
        ) : null}
      </div>

      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-semibold">일별 추이</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {loading && !report
            ? <div role="status" aria-label="방문 집계 로딩 중" className="h-44 animate-pulse rounded-md bg-muted" />
            : <VisitsBarChart days={report?.days ?? []} />}
        </CardContent>
      </Card>

      <VisitsTopTables paths={report?.topPaths ?? []} referrers={report?.topReferrers ?? []} />
    </div>
  );
}
